import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const OfferingCard = ({ offering }) => {
  const handleContact = (contactType, contactValue) => {
    if (contactType === 'phone') {
      window.open(`tel:${contactValue}`, '_self');
    } else if (contactType === 'email') {
      window.open(`mailto:${contactValue}`, '_self');
    } else if (contactType === 'website') {
      window.open(contactValue, '_blank', 'noopener,noreferrer');
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'accommodation':
        return 'Bed';
      case 'hospitality':
        return 'UtensilsCrossed';
      case 'local-products':
        return 'ShoppingBag';
      default:
        return 'MapPin';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'accommodation':
        return 'text-accent';
      case 'hospitality':
        return 'text-success';
      case 'local-products':
        return 'text-warning';
      default:
        return 'text-primary';
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-card overflow-hidden card-hover transition-all duration-normal">
      {/* Image Section */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <Image
          src={offering?.image}
          alt={offering?.imageAlt || offering?.name}
          className="w-full h-full object-cover" />

        
        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <div className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-2.5 py-1.5 rounded-full">
            <Icon
              name={getCategoryIcon(offering?.category)}
              size={14}
              className={getCategoryColor(offering?.category)} />

            <span className="text-xs font-medium text-foreground capitalize">
              {offering?.category?.replace('-', ' ')}
            </span>
          </div>
        </div>

        {/* Availability Status */}
        {offering?.availability &&
        <div className="absolute top-3 right-3">
            <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
          offering?.availability === 'available' ? 'bg-success/90 text-success-foreground' :
          offering?.availability === 'limited' ? 'bg-warning/90 text-warning-foreground' : 'bg-destructive/90 text-destructive-foreground'}`
          }>
              {offering?.availability === 'available' ? 'Available' :
            offering?.availability === 'limited' ? 'Limited' : 'Booked'}
            </div>
          </div>
        }

        {/* Price Badge */}
        {offering?.price &&
        <div className="absolute bottom-3 right-3">
            <div className="bg-primary/90 text-primary-foreground px-3 py-1.5 rounded-full">
              <span className="text-sm font-semibold">€{offering?.price}</span>
              {offering?.priceUnit &&
            <span className="text-xs opacity-90">/{offering?.priceUnit}</span>
            }
            </div>
          </div>
        }
      </div>
      {/* Content Section */}
      <div className="p-5">
        {/* Header */}
        <div className="mb-3">
          <h3 className="font-heading font-semibold text-lg text-foreground mb-1 line-clamp-1">
            {offering?.name}
          </h3>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Icon name="MapPin" size={14} />
            <span className="text-sm">{offering?.location}</span>
          </div>
        </div>

        {/* Description */}
        <p className="text-muted-foreground text-sm leading-relaxed mb-4 line-clamp-2">
          {offering?.description}
        </p>

        {/* Features/Amenities */}
        {offering?.features && offering?.features?.length > 0 &&
        <div className="mb-4">
            <div className="flex flex-wrap gap-1.5">
              {offering?.features?.slice(0, 3)?.map((feature, index) =>
            <span
              key={index}
              className="inline-flex items-center gap-1 bg-muted px-2 py-1 rounded-md text-xs text-muted-foreground">

                  <Icon name="Check" size={10} className="text-success" />
                  {feature}
                </span>
            )}
              {offering?.features?.length > 3 &&
            <span className="text-xs text-muted-foreground px-2 py-1">
                  +{offering?.features?.length - 3} more
                </span>
            }
            </div>
          </div>
        }

        {/* Rating */}
        {offering?.rating &&
        <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-1">
              {[...Array(5)]?.map((_, i) =>
            <Icon
              key={i}
              name="Star"
              size={14}
              className={i < Math.floor(offering?.rating) ? 'text-warning fill-current' : 'text-muted'} />

            )}
            </div>
            <span className="text-sm font-medium text-foreground">{offering?.rating}</span>
            {offering?.reviewCount &&
          <span className="text-xs text-muted-foreground">
                ({offering?.reviewCount} reviews)
              </span>
          }
          </div>
        }

        {/* Certifications */}
        {offering?.certifications && offering?.certifications?.length > 0 &&
        <div className="mb-4">
            <div className="flex items-center gap-2">
              <Icon name="Award" size={14} className="text-accent" />
              <span className="text-xs text-muted-foreground">
                {offering?.certifications?.join(', ')}
              </span>
            </div>
          </div>
        }

        {/* Contact Actions */}
        <div className="flex flex-col sm:flex-row gap-2">
          {offering?.website &&
          <Button
            variant="default"
            size="sm"
            onClick={() => handleContact('website', offering?.website)}
            className="flex-1"
            iconName="ExternalLink"
            iconPosition="right"
            iconSize={14}>

              Visit Website
            </Button>
          }
          
          <div className="flex gap-2">
            {offering?.phone &&
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleContact('phone', offering?.phone)}
              iconName="Phone"
              iconSize={14}
              className="sm:px-3">

                <span className="hidden sm:inline">Call</span>
              </Button>
            }
            
            {offering?.email &&
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleContact('email', offering?.email)}
              iconName="Mail"
              iconSize={14}
              className="sm:px-3">

                <span className="hidden sm:inline">Email</span>
              </Button>
            }
          </div>
        </div>
      </div>
    </div>);

};

export default OfferingCard;